// Local-only floating combat numbers: a pixelated damage/Miss/Block popup at a screen position. Driven
// by the CombatHit handler in world3d for events involving the LOCAL player only (so they're private
// to each player — no extra networking; the popup is purely client-side rendering of the shared hit).
//
//   popCombatNumber(screenX, screenY, { kind: "deal"|"take"|"miss"|"block", amount, crit })
//
// Crit hits get an "explosive" anime-style starburst behind the number.

const LAYER_ID = "gl-dmg-layer";

function ensureLayer(): HTMLElement {
  let layer = document.getElementById(LAYER_ID);
  if (layer) return layer;
  const style = document.createElement("style");
  style.textContent = `
  #${LAYER_ID} { position: fixed; inset: 0; pointer-events: none; overflow: hidden; z-index: 40; }
  #${LAYER_ID} .gl-dmg {
    position: absolute; transform: translate(-50%, -50%);
    font-family: ui-monospace, "DejaVu Sans Mono", monospace; font-weight: 800;
    font-size: 20px; line-height: 1; letter-spacing: 1px; white-space: nowrap;
    image-rendering: pixelated; -webkit-font-smoothing: none;
    /* blocky pixel outline (4-way hard shadow) */
    text-shadow: -2px 0 #000, 2px 0 #000, 0 -2px #000, 0 2px #000, -2px -2px #000, 2px 2px #000, 2px -2px #000, -2px 2px #000;
    animation: gl-dmg-float 0.9s ease-out forwards; will-change: transform, opacity;
  }
  #${LAYER_ID} .gl-dmg.deal  { color: #ffffff; }
  #${LAYER_ID} .gl-dmg.take  { color: #ff5a4a; }
  #${LAYER_ID} .gl-dmg.miss  { color: #b9c0cc; font-size: 16px; }
  #${LAYER_ID} .gl-dmg.block { color: #6fd0ff; font-size: 16px; }
  #${LAYER_ID} .gl-dmg.crit  { color: #ffd23f; font-size: 30px; animation: gl-dmg-floatcrit 1.05s ease-out forwards; }
  /* explosive anime starburst behind a crit */
  #${LAYER_ID} .gl-dmg.crit::before {
    content: ""; position: absolute; left: 50%; top: 50%; width: 2.4em; height: 2.4em;
    transform: translate(-50%, -50%) rotate(0deg); z-index: -1;
    background: radial-gradient(circle at 50% 50%, rgba(255,210,63,.95) 0%, rgba(255,120,30,.8) 35%, rgba(255,60,20,0) 70%);
    clip-path: polygon(50% 0%, 61% 33%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 33%);
    animation: gl-dmg-burst 0.5s ease-out forwards;
  }
  @keyframes gl-dmg-float { 0% { opacity: 0; transform: translate(-50%, -50%) scale(.7); } 12% { opacity: 1; transform: translate(-50%, -120%) scale(1.1); } 30% { transform: translate(-50%, -150%) scale(1); } 100% { opacity: 0; transform: translate(-50%, -260%) scale(1); } }
  @keyframes gl-dmg-floatcrit { 0% { opacity: 0; transform: translate(-50%, -50%) scale(.5); } 14% { opacity: 1; transform: translate(-50%, -130%) scale(1.5); } 34% { transform: translate(-50%, -165%) scale(1.05); } 100% { opacity: 0; transform: translate(-50%, -290%) scale(1.05); } }
  @keyframes gl-dmg-burst { 0% { opacity: 0; transform: translate(-50%, -50%) scale(.2) rotate(-25deg); } 25% { opacity: 1; transform: translate(-50%, -50%) scale(1.5) rotate(8deg); } 100% { opacity: 0; transform: translate(-50%, -50%) scale(2.1) rotate(20deg); } }
  `;
  document.head.appendChild(style);
  layer = document.createElement("div");
  layer.id = LAYER_ID;
  document.body.appendChild(layer);
  return layer;
}

export type CombatNumberKind = "deal" | "take" | "miss" | "block";

export function popCombatNumber(
  screenX: number,
  screenY: number,
  opts: { kind: CombatNumberKind; amount?: number; crit?: boolean },
): void {
  const layer = ensureLayer();
  const el = document.createElement("div");
  const crit = !!opts.crit && (opts.kind === "deal" || opts.kind === "take");
  el.className = `gl-dmg ${crit ? "crit" : opts.kind}`;
  el.textContent =
    opts.kind === "miss" ? "MISS" :
    opts.kind === "block" ? "BLOCK" :
    String(Math.max(0, Math.round(opts.amount ?? 0)));
  // tiny horizontal jitter so rapid hits don't perfectly overlap
  el.style.left = `${screenX + (Math.random() * 18 - 9)}px`;
  el.style.top = `${screenY}px`;
  layer.appendChild(el);
  el.addEventListener("animationend", () => el.remove(), { once: true });
  // safety cleanup in case the animationend doesn't fire (tab backgrounded mid-anim)
  window.setTimeout(() => el.remove(), 1300);
}
